import React, { useRef, useState, Suspense, useCallback, useEffect } from "react";
import Placeholder from "../Placeholder";
import useCompileReady from "../useCompileReady";
import { useGLTF, useAnimations, useCursor, Html } from "@react-three/drei";
import { Select } from "@react-three/postprocessing";
import { extend, useFrame, useThree } from "@react-three/fiber";
import { geometry } from "maath";
extend(geometry);
import { useProjectFocus } from "@/stores/projectFocusStore";
import { computeFocusFrame } from "../focusUtils";
import useFocusOcclusionHidden from "../useFocusOcclusionHidden";
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
} from "@nextui-org/react";

// Simple debounce utility
function debounce(fn, ms) {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), ms);
  };
}

// Inner component that actually loads and renders the model with animation
// This suspends, and is wrapped by Suspense in the parent component
function VArchModelInner({
  projectName,
  modelPath,
  link,
  animationSpeed = 1,
  // Focus-view azimuth for this model, radians around the vertical axis
  // (negative = clockwise seen from above). 0 = straight front view.
  focusYaw = 0,
  // Multiplier on the auto-fit focus zoom. >1 zooms in closer; use to fix
  // models whose bounding box is inflated by stray geometry in the GLB.
  focusZoom = 1,
  ...props
}) {
  const groupRef = useRef();
  const { size } = useThree();
  const { scene, animations } = useGLTF(modelPath);
  const { actions, names } = useAnimations(animations, groupRef);
  // GLTF meshes default to castShadow=false; enable so the accumulated
  // ground shadows include these models.
  React.useMemo(() => {
    scene.traverse((child) => {
      if (child.isMesh) child.castShadow = true;
    });
  }, [scene]);
  // Stay hidden (placeholder up) until shaders are compiled off-thread, so
  // the first draw never stutters the visible scene.
  const compiled = useCompileReady(scene);

  // Hover state
  const [hovered, hover] = useState(null);
  // Debounce hover a bit to stop the ticker from being erratic
  const debouncedHover = useCallback(debounce(hover, 30), []);
  const over = (name) => (e) => (e.stopPropagation(), debouncedHover(name));
  useCursor(hovered);

  // Modal state
  const [isOpen, setIsOpen] = useState(false);

  const focusId = `varch:${projectName}`;
  // Hide this model while it blocks the view of another focused model.
  const occluded = useFocusOcclusionHidden(focusId, groupRef);

  const openModal = () => {
    setTimeout(() => {
      setIsOpen(true);
    }, 0);
  };

  // First click focuses the camera on the model; clicking it again while
  // focused opens the project modal (same as the card's action button).
  const handleMeshClick = (e) => {
    e.stopPropagation();
    const { focused, setFocus } = useProjectFocus.getState();
    if (focused?.id === focusId) {
      openModal();
      return;
    }
    const frame = computeFocusFrame(groupRef.current, size);
    setFocus({
      id: focusId,
      name: projectName,
      ...frame,
      zoom: frame.zoom * focusZoom,
      yaw: focusYaw,
      actionLabel: "View project",
      onAction: openModal,
    });
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  // Play all GLTF animations when component mounts
  useEffect(() => {
    if (actions && names.length > 0) {
      // Play all animations found in the GLTF file
      names.forEach((name) => {
        const action = actions[name];
        if (action) {
          action.reset().play();
          action.timeScale = animationSpeed;
        }
      });
    }
  }, [actions, names, animationSpeed]);

  return (
    <>
      {!compiled && (
        <Placeholder position={props.position || [0, 0.5, 0]} scale={[1, 1, 1]} />
      )}
      <group
        ref={groupRef}
        dispose={null}
        visible={compiled && !occluded}
        {...props}
      >
        <Select enabled={hovered}>
          <primitive
            object={scene}
            onPointerOver={() => hover(true)}
            onPointerOut={() => hover(false)}
            onPointerDown={(e) => e.stopPropagation()}
            onPointerUp={(e) => e.stopPropagation()}
            onClick={handleMeshClick}
          >
            <Html>
              {hovered && !isOpen && (
                <div className="content">{projectName}</div>
              )}
            </Html>
          </primitive>
        </Select>
      </group>

      {/* Only render the modal Html when open to avoid event conflicts */}
      {isOpen && (
        <Html portal={{ current: document.body }} zIndexRange={[100, 0]}>
          <Modal
            size="5xl"
            isOpen={isOpen}
            onClose={handleClose}
            backdrop="blur"
            scrollBehavior="inside"
            placement="center"
            isDismissable={true}
            hideCloseButton={false}
          >
            <ModalContent>
              <ModalHeader className="flex flex-col gap-1">
                {projectName}
              </ModalHeader>
              <ModalBody>
                <div className="flex flex-col items-center justify-center gap-4 p-8">
                  <p className="text-center">View this project at:</p>
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-500 hover:text-blue-700 underline text-lg break-all"
                  >
                    {link}
                  </a>
                </div>
              </ModalBody>
              <ModalFooter>
                <Button color="danger" variant="light" onClick={handleClose}>
                  Close
                </Button>
                <Button
                  color="primary"
                  as="a"
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open Link
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>
        </Html>
      )}
    </>
  );
}

// Wrapper component that provides Suspense boundary
// This allows each model to load independently with its own placeholder
export default function VArchModel(props) {
  return (
    <Suspense
      fallback={
        <Placeholder
          position={props.position || [0, 0.5, 0]}
          scale={[1, 1, 1]}
        />
      }
    >
      <VArchModelInner {...props} />
    </Suspense>
  );
}
